// Spaced-repetition scheduling — pure logic, no Firebase (so it's unit-testable
// in isolation). Expanding intervals adjusted by the last checkpoint score:
// a pass advances to the next interval; a fail resets to relearn soon. This
// operationalises the two strongest, best-evidenced effects (spacing + retrieval)
// without the weight of a full FSRS implementation.
//
// NOTE: the interval ladder is a TUNABLE PRODUCT PARAMETER, not a scientific
// constant — calibrate against retention data once we have it.
export const REVIEW_INTERVALS_DAYS = [1, 3, 7, 16, 35, 70] as const;

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_PASS_THRESHOLD = 70; // mirrors CHECKPOINT_PASS_THRESHOLD

export interface ReviewSchedulingState {
  intervalIndex: number;
  lastScore: number;
  lastReviewedAt: string; // ISO
  nextReviewAt: string;   // ISO
}

// A persisted review item (reviews/{userId}/items/{pathId__nodeId}).
export interface ReviewItem extends ReviewSchedulingState {
  userId: string;
  contentId: string;
  pathId: string;
  nodeId: string;
  conceptIds: string[];
  label: string; // short display label (e.g. the checkpoint question)
}

/**
 * Compute the next review schedule from the previous interval index and the
 * score just earned. Pass → advance one rung (capped); fail → reset to rung 0.
 * First-ever scheduling starts at rung 1 (3 days) on a pass, rung 0 (1 day) on a fail.
 */
export function scheduleNextReview(
  prev: { intervalIndex: number } | null,
  score: number,
  now: Date,
  passThreshold: number = DEFAULT_PASS_THRESHOLD,
): ReviewSchedulingState {
  const passed = score >= passThreshold;
  const lastIndex = REVIEW_INTERVALS_DAYS.length - 1;

  let intervalIndex: number;
  if (prev == null) {
    intervalIndex = passed ? 1 : 0;
  } else if (passed) {
    intervalIndex = Math.min(prev.intervalIndex + 1, lastIndex);
  } else {
    intervalIndex = 0;
  }

  const days = REVIEW_INTERVALS_DAYS[intervalIndex];
  return {
    intervalIndex,
    lastScore: score,
    lastReviewedAt: now.toISOString(),
    nextReviewAt: new Date(now.getTime() + days * DAY_MS).toISOString(),
  };
}
